import type { OrganizationSubscriptionResponse } from 'generatedTypes/user/models';
import { useCallback, useEffect } from 'react';

import useGlobalStore from 'hooks/useGlobalStore';
import useStripe from 'hooks/useStripe';

import { HAS_FETCHED_KEY, IS_FETCHING_KEY, NAMESPACE, STRIPE_SUBSCRIPTIONS_KEY } from './constants';
import type { StripeSubscriptionsResult } from './types';

const useStripeSubscriptions = (): StripeSubscriptionsResult => {
    const { getValue, setValue } = useGlobalStore({ namespace: NAMESPACE });
    const { fetchStripeSubscriptions } = useStripe();

    const stripeSubscriptions =
        (getValue(STRIPE_SUBSCRIPTIONS_KEY) as OrganizationSubscriptionResponse[] | null) ?? null;
    const hasFetched = Boolean(getValue(HAS_FETCHED_KEY));
    const isFetching = Boolean(getValue(IS_FETCHING_KEY));

    const fetchAndCache = useCallback(async () => {
        setValue({ [IS_FETCHING_KEY]: true });

        try {
            const response = await fetchStripeSubscriptions();

            setValue({ [STRIPE_SUBSCRIPTIONS_KEY]: response ?? [] });
            setValue({ [IS_FETCHING_KEY]: false });
            setValue({ [HAS_FETCHED_KEY]: true });
        } catch (error) {
            console.error('Failed to load Stripe subscriptions:', error);

            setValue({ [STRIPE_SUBSCRIPTIONS_KEY]: [] });
            setValue({ [IS_FETCHING_KEY]: false });
            setValue({ [HAS_FETCHED_KEY]: true });
        }
    }, [fetchStripeSubscriptions, setValue]);

    useEffect(() => {
        if (hasFetched || isFetching) {
            return;
        }

        fetchAndCache();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        hasFetched,
        isLoading: isFetching || !hasFetched,
        refetch: fetchAndCache,
        stripeSubscriptions
    };
};

export default useStripeSubscriptions;
